import { AlertIconLight } from "@images/svgIcons/alertIcon"
import { word } from "@modules/core/utils/i18n"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { useProgramSubscriberActions } from "@modules/programs/programSubscriber/hooks/useProgramSubscriber"
import { ProgramSubscriptionStatus } from "@modules/programs/programTypes"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { HUDialog } from "@modules/ui/components/popup/huDialog"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { useCallback, useEffect, useImperativeHandle, useState } from "react"
import { useTheme } from "styled-components"

export type ProgramSubscriberDeleteModalRef = {
  open: () => void
}

export type ProgramSubscriberDeleteModalProps = {
  subscriberDetails: ProgramSubscriberEntity | ProgramBundleSubscriberEntity
  programId: string
  ref: React.Ref<ProgramSubscriberDeleteModalRef>
}

export function ProgramSubscriberDeleteModal({ subscriberDetails, programId, ref }: ProgramSubscriberDeleteModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [isSelected, setIsSelected] = useState({
    now: false,
    future: false,
  })

  const theme = useTheme()
  const toast = useToast()
  const { sidebarNavigateBack } = useSidebar()
  const { removeSubscriber } = useProgramSubscriberActions(programId, subscriberDetails)

  useEffect(() => {
    if (subscriberDetails.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION) {
      setIsSelected({ now: true, future: false })
    }
  }, [showModal, subscriberDetails.subscriptionStatus])

  const onHide = useCallback(() => {
    setIsSelected({ now: false, future: false })
    setShowModal(false)
  }, [])

  const onSubmit = async () => {
    setIsSubmitting(true)
    await removeSubscriber(isSelected.future)
      .then(() => {
        toast.show({
          severity: "success",
          summary: word("program.subscribers.subscriptionDetails.remove.success"),
        })

        onHide()
        sidebarNavigateBack()
      })
      .catch(() => {
        toast.show({
          severity: "error",
          summary: word("program.subscribers.subscriptionDetails.remove.error"),
        })
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  const onSelectNow = () => {
    setIsSelected({ now: true, future: false })
  }

  const onSelectFuture = () => {
    if (subscriberDetails.subscriptionStatus !== ProgramSubscriptionStatus.PENDING_CANCELLATION)
      setIsSelected({ now: false, future: true })
    else
      toast.show({
        severity: "error",
        summary: word("program.subscribers.subscriptionDetails.remove.pending_cancellation_error"),
      })
  }

  return (
    <HUDialog
      visible={showModal}
      header={null}
      onHide={onHide}
      style={{ width: "480px", padding: 0 }}
      contentClassName="p-0"
    >
      <div className="flex flex-column gap-5 bg-neutralColor-100 px-5">
        {/* HEADER */}
        <div className="flex flex-column flex-wrap pt-4 gap-2 justify-content-center align-content-center text-center">
          <div className="flex w-full justify-content-center align-content-center">
            <AlertIconLight color={theme.error} />
          </div>
          <HUText fontStyle="TL" color={theme.neutralColor900}>
            {word("program.subscribers.subscriptionDetails.remove.title")}
          </HUText>
        </div>
        <HUText
          fontStyle="BM"
          className="flex justify-content-center align-content-center text-center"
          color={theme.neutralColor600}
        >
          {word("program.subscribers.subscriptionDetails.remove.description")}
        </HUText>

        {/* CONTENT */}
        <div className="flex gap-3">
          <div
            className={`flex flex-column w-6 p-3 gap-3 cursor-pointer border-1 border-round-2xl ${isSelected.now ? "bg-white border-primary-500" : "cursor-pointer border-neutralColor-700 "}`}
            onClick={onSelectNow}
          >
            <HUText fontStyle="TS" className="text-center" color={theme.neutralColor900}>
              {word("program.subscribers.subscriptionDetails.remove.now")}
            </HUText>
          </div>

          <div
            className={`flex flex-column w-6 p-3 gap-3 cursor-pointer border-1 border-round-2xl ${isSelected.future ? "bg-white border-primary-500" : "cursor-pointer border-neutralColor-700"}`}
            onClick={onSelectFuture}
          >
            <HUText fontStyle="TS" className="  text-center" color={theme.neutralColor900}>
              {word("program.subscribers.subscriptionDetails.remove.future")}
            </HUText>
            <HUText fontStyle="BS" className=" text-center" color={theme.neutralColor700}>
              {` ${word("program.subscribers.subscriptionDetails.remove.ending")} ${formatDateWithHyphen(dayjs(subscriberDetails.periodEndDate).format("L"))}`}
            </HUText>
          </div>
        </div>

        {/* BUTTON */}
        <div className="mb-3 flex flex-row gap-2 align-items-center justify-content-center">
          <HUButton
            type="Left_Icon"
            size="M"
            colorType="Quaternary"
            text={word("global.cancel")}
            onClick={(event) => {
              event.preventDefault()
              setShowModal(false)
            }}
            className="w-full"
          />
          <HUButton
            type="Right_Icon"
            size="M"
            colorType="Primary"
            text={word("global.remove.standard")}
            onClick={(event) => {
              event.preventDefault()
              onSubmit()
            }}
            disabled={(!isSelected.future && !isSelected.now) || isSubmitting}
            className="w-full"
          />
        </div>
      </div>
    </HUDialog>
  )
}
